import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Select as FormSelect, DateTimeInput as FormDateTimeInput } from '../FormField';
import RichTextEditor from '../ui/RichTextEditor';
import RecordActivity from '../ui/RecordActivity';
import {
  Task, Priority, TaskStatus, BlockedOn, TaskType, TEAM_OPTIONS, loadAssigneeOptions, fmtDueDate,
  loadTasks, getBlockingTaskIds, archiveTask, restoreTask,
} from '../../services/TasksService';
import { useFormSaveEvent, useClearErrorsOnEdit } from './_shared';
import TagMultiSelect from '../ui/TagMultiSelect';
import SearchableSelect from '../ui/SearchableSelect';
import Icon from '../Icon';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../NotificationProvider';
import { useConfirm } from '../ui/ConfirmProvider';
import { recordLabel } from '../../utils/recordId';
import { dependencyLabels, dependencyIdsFromLabels } from '../../utils/dependencyLabels';
import { StoredEvent, loadEvents, EVENTS_CHANGED_EVENT } from '../../services/EventsService';
import { useRecordRefs } from '../../utils/recordRefs';

// The same words the task status picker offers, so a logged transition reads
// in the vocabulary the user just chose.
const TASK_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};

// The API's name for each field, where it differs from the form's. A 422 names
// `due_date`; the form calls the same field `dueDateISO`.
const TASK_API_FIELD: Record<string, string> = {
  ownerTeam: 'owner_team', dueDateISO: 'due_date', startDateISO: 'start_date',
  startTime: 'start_time', endTime: 'end_time', eventIds: 'event_ids',
  parentTaskId: 'parent_task_id', dependsOnTaskIds: 'depends_on_task_ids',
};

const TASK_PRIORITIES: Priority[] = ['P0', 'P1', 'P2', 'P3'];
const TASK_STATUSES: TaskStatus[] = ['Not Started', 'In Progress', 'Done'];

const TASK_PRIORITY_LABELS: Record<Priority, string> = {
  P0: 'P0 — Critical',
  P1: 'P1 — High',
  P2: 'P2 — Medium',
  P3: 'P3 — Low',
};

const TASK_PRIORITY_COLORS: Record<Priority, string> = {
  P0: '#dc2626',
  P1: '#f59e0b',
  P2: '#3b82f6',
  P3: '#9ca3af',
};


interface TaskFormPageProps {
  task?: Task | null;
  onClose: () => void;
  /** May return a promise: if it rejects, the shared hook shows the failure
   *  (a 422's field errors under their fields, otherwise a toast) and the form
   *  stays open. Let it throw rather than catching and toasting in the parent. */
  onSave: (data: Omit<Task, 'id' | 'isCustom'>) => void | Promise<unknown>;
  onNameLoad?: (name: string) => void;
}

export const TaskFormPage: React.FC<TaskFormPageProps> = ({ task, onClose, onSave, onNameLoad }) => {
  // Re-render when the record numbers (TA-012 instead of a uuid) load.
  useRecordRefs();
  const isDirtyRef = useRef(false);
  const lastTaskIdRef = useRef<string | undefined>(undefined);
  const formTopRef = useRef<HTMLDivElement>(null);

  const [tfForm, setTfForm] = useState({
    task: '',
    priority: 'P1' as Priority,
    status: 'Not Started' as TaskStatus,
    blockedOn: null as BlockedOn,
    ownerTeam: '',
    assignees: [] as string[],
    dueDateISO: '',
    startDateISO: '',
    startTime: '',
    endTime: '',
    milestone: '',
    deliverable: '',
    notes: '',
    teamTag: '',
    phase: '' as string,
    taskType: 'task' as TaskType,
    eventIds: [] as string[],
    parentTaskId: '' as string,
    dependsOnTaskIds: [] as string[],
  });
  // Narrows to members tagged with the selected Owner Team — recomputed as
  // the user changes it, and falls back to everyone if that team has no
  // tagged members yet (see loadAssigneeOptions).
  const assigneeOptions = useMemo(() => loadAssigneeOptions(tfForm.ownerTeam || undefined), [tfForm.ownerTeam]);
  const [tfErrors, setTfErrors] = useState<Record<string, string>>({});
  const [projectEvents, setProjectEvents] = useState<StoredEvent[]>([]);
  const allTasks = loadTasks();
  const otherTasks = allTasks.filter(t => t.id !== task?.id);
  const taskLabel = (t: Task) => `${recordLabel('task', t.id)} — ${t.task}`;
  const childTasks = task ? allTasks.filter(t => t.parentTaskId === task.id) : [];
  const blockingTaskIds = task ? getBlockingTaskIds(task) : [];
  // Reverse of "Depends On": tasks that name THIS one in their own
  // dependsOnTaskIds. Without this, a task blocking others shows nothing on
  // its own page — the relationship is only visible from the blocked task.
  const dependentTasks = task
    ? otherTasks.filter(t => (t.dependsOnTaskIds ?? []).includes(task.id))
    : [];
  // Keyed off the task as it loaded, not the in-progress form — so marking a
  // task Done and filling in its last few fields in the same edit still
  // works, but reopening an already-Done task later shows it locked. Status
  // itself stays editable (via the header's status stepper) so a wrongly-set
  // Done can be reopened; everything else is comment-only past this point.
  const isDoneLocked = task?.status === 'Done';
  // Archived tasks (e.g. reached via a direct /task/TA-xxx link, or the
  // Tasks toolbar's Archived/All filter) are viewable but not editable —
  // restore first if it needs changing.
  const isArchivedLocked = !!task?.archived;
  const isLocked = isDoneLocked || isArchivedLocked;
  // Archived tasks can still be looked up (for labels/history) but shouldn't
  // be newly pickable as a parent or dependency going forward.
  const pickableTasks = otherTasks.filter(t => !t.archived);

  const { user } = useAuth();
  const { showError } = useNotifications();
  const confirm = useConfirm();
  useEffect(() => {
    const update = () => { try { setProjectEvents(loadEvents()); } catch { /* ignore */ } };
    update();
    window.addEventListener(EVENTS_CHANGED_EVENT, update);
    return () => window.removeEventListener(EVENTS_CHANGED_EVENT, update);
  }, []);

  useEffect(() => {
    if (!task) return;
    const isNewTask = task.id !== lastTaskIdRef.current;
    // Skip re-init if same task and user has already edited — prevents background refresh stomping edits
    if (!isNewTask && isDirtyRef.current) return;
    lastTaskIdRef.current = task.id;
    isDirtyRef.current = false;

    const initialStatus = task.status || 'Not Started';
    const initialBlockedOn = task.blockedOn ?? null;
    if (task.task) onNameLoad?.(task.task);
    setTfForm({
      task: task.task,
      priority: task.priority,
      status: initialStatus,
      blockedOn: initialBlockedOn,
      ownerTeam: task.ownerTeam || '',
      assignees: task.assignees ?? [],
      dueDateISO: task.dueDateISO || '',
      startDateISO: task.startDateISO || '',
      startTime: (task as any).startTime || '',
      endTime: (task as any).endTime || '',
      milestone: task.milestone || '',
      deliverable: task.deliverable || '',
      notes: task.notes || '',
      teamTag: task.teamTag || '',
      phase: (task as any).phase || '',
      taskType: task.taskType ?? 'task',
      eventIds: task.eventIds ?? [],
      parentTaskId: task.parentTaskId ?? '',
      dependsOnTaskIds: task.dependsOnTaskIds ?? [],
    });
    window.dispatchEvent(new CustomEvent('task-form-status-loaded', { detail: initialStatus }));
    window.dispatchEvent(new CustomEvent('task-form-blockedOn-loaded', { detail: initialBlockedOn ?? '' }));
  }, [task]);

  useEffect(() => {
    const handler = (e: Event) => setTfForm(f => ({ ...f, status: (e as CustomEvent<string>).detail as TaskStatus }));
    window.addEventListener('task-form-status-change', handler);
    return () => window.removeEventListener('task-form-status-change', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const raw = (e as CustomEvent<string>).detail;
      setTfForm(f => ({ ...f, blockedOn: (raw || null) as BlockedOn }));
    };
    window.addEventListener('task-form-blockedOn-change', handler);
    return () => window.removeEventListener('task-form-blockedOn-change', handler);
  }, []);


  const setTf = (field: string, value: string) => {
    isDirtyRef.current = true;
    setTfForm(f => ({ ...f, [field]: value }));
  };

  const handleTaskSave = useCallback(() => {
    const e: Record<string, string> = {};
    if (!tfForm.task.trim()) e.task = 'Task name is required';
    // Priority always carries a value (button-group with a default, never
    // clearable), so "required" is already satisfied structurally — no
    // check needed here. See PR description for the reasoning.
    if (!tfForm.phase) e.phase = 'Phase is required';
    // Only enforced when there is at least one event to choose from — with
    // none created yet, the field itself renders a "create an event first"
    // hint instead of the picker, so there is nothing valid to require.
    if (projectEvents.length > 0 && tfForm.eventIds.length === 0) e.eventIds = 'Event is required';
    if (!tfForm.milestone.trim()) e.milestone = 'Milestone is required';
    if (!tfForm.deliverable.trim()) e.deliverable = 'Deliverable is required';
    if (!tfForm.ownerTeam) e.ownerTeam = 'Owner Team is required';
    if (tfForm.assignees.length === 0) e.assignees = 'At least one assignee is required';
    if (!tfForm.startDateISO) e.startDateISO = 'Start date is required';
    if (!tfForm.dueDateISO) e.dueDateISO = 'Due date is required';
    // Compares ISO date strings (yyyy-mm-dd), which sort lexicographically —
    // same-day is allowed (a task can start and finish the same day), only
    // a due date strictly before its own start date is rejected.
    if (tfForm.startDateISO && tfForm.dueDateISO && tfForm.dueDateISO < tfForm.startDateISO) {
      e.dueDateISO = 'Due date must be on or after the start date';
    }
    setTfErrors(e);
    if (Object.keys(e).length > 0) {
      // Validation used to fail completely silently beyond an inline red
      // label next to each field — easy to miss, especially for a field
      // below the fold (Starts/Due). A save that visibly does nothing (no
      // toast, no navigation) reads as "my changes didn't persist" rather
      // than "the form is telling you something," which is exactly what
      // happened here: pre-existing tasks (240+ of them) were missing one
      // or more of these newly-required fields the moment this validation
      // shipped, and nothing told the user why saving them stopped working.
      showError(`Can't save — missing: ${Object.values(e).join(', ')}`);
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const dueDate = tfForm.dueDateISO ? fmtDueDate(tfForm.dueDateISO) : undefined;
    return onSave({
      task: tfForm.task.trim(),
      priority: tfForm.priority,
      status: tfForm.status,
      blockedOn: tfForm.blockedOn,
      ownerTeam: tfForm.ownerTeam,
      assignees: tfForm.assignees,
      dueDate,
      dueDateISO: tfForm.dueDateISO || undefined,
      startDateISO: tfForm.startDateISO || undefined,
      startTime: tfForm.startTime || undefined,
      endTime: tfForm.endTime || undefined,
      milestone: tfForm.milestone || undefined,
      deliverable: tfForm.deliverable || undefined,
      notes: tfForm.notes || undefined,
      teamTag: tfForm.teamTag.trim() || undefined,
      phase: tfForm.phase || undefined,
      taskType: tfForm.taskType,
      eventIds: tfForm.eventIds.length > 0 ? tfForm.eventIds : undefined,
      parentTaskId: tfForm.parentTaskId || undefined,
      dependsOnTaskIds: tfForm.dependsOnTaskIds.length > 0 ? tfForm.dependsOnTaskIds : undefined,
    } as any);
  }, [tfForm, onSave]);

  const fieldErrors = useFormSaveEvent(handleTaskSave, { fallback: 'Failed to save the task. Please try again.' });
  useClearErrorsOnEdit(tfForm, fieldErrors, TASK_API_FIELD);
  // The form's own required-field checks come first; otherwise the server's
  // message for the same field (looked up by the API's name for it).
  const fieldError = (key: string): string | undefined =>
    tfErrors[key] ?? fieldErrors.errorFor(TASK_API_FIELD[key] ?? key);

  // Archive from the breadcrumb action bar. Soft-delete only — the task leaves
  // the list but stays recoverable, matching how vendors and decor elements
  // behave. Confirmed first because it is not obvious from the button alone
  // that the task disappears from every view at once.
  useEffect(() => {
    const onArchive = async () => {
      if (!task?.id) return;
      const ok = await confirm({
        title: `Archive "${task.task}"?`,
        description: 'It will be removed from the task list. You can restore it later.',
        confirmLabel: 'Archive',
      });
      if (!ok) return;
      try {
        await archiveTask(task.id);
        isDirtyRef.current = false;
        onClose();
      } catch {
        showError('Could not archive the task. Please try again.');
      }
    };
    window.addEventListener('task-form-archive', onArchive);
    return () => window.removeEventListener('task-form-archive', onArchive);
  }, [task, onClose, showError, confirm]);

  useEffect(() => {
    const onRestore = async () => {
      if (!task?.id) return;
      try {
        await restoreTask(task.id);
      } catch {
        showError('Could not restore the task. Please try again.');
      }
    };
    window.addEventListener('task-form-restore', onRestore);
    return () => window.removeEventListener('task-form-restore', onRestore);
  }, [task, showError]);

  return (
    <div className="detail-layout">
      <div className="tf-main-stack">
      <div className="inline-form-page" ref={formTopRef}>
        {isLocked && (
          <div className="entity-state-banner entity-state-banner--locked">
            {isArchivedLocked
              ? 'Archived — fields are locked. Restore it from the header to edit, or leave a comment below.'
              : 'Done — fields are locked. Reopen it (change status above) to edit, or leave a comment below.'}
          </div>
        )}
        <FormInput
          label="Task Name"
          type="text"
          placeholder="Describe the task..."
          value={tfForm.task}
          onChange={e => setTf('task', e.target.value)}
          error={fieldError('task')}
          autoFocus
          required
          disabled={isLocked}
        />

        {/* Priority and Phase share one row. This was an ad-hoc inline flex,
            which meant it never picked up .form-row's 640px collapse — the two
            fields stayed side by side on a phone and the P0–P3 chips
            truncated. Priority takes twice the width because it is four chips
            rather than one select. */}
        <div className="form-row tf-priority-phase-row">
          <div className="form-group tf-priority-field">
            <label className="form-label">Priority<span className="form-required">*</span></label>
            <div className="tf-priority-row">
              {TASK_PRIORITIES.map(p => (
                <button
                  key={p}
                  className={`tf-priority-btn${tfForm.priority === p ? ' active' : ''}`}
                  style={{ /* ds-dynamic: priority color from map */
                    borderColor: tfForm.priority === p ? TASK_PRIORITY_COLORS[p] : undefined,
                    background: tfForm.priority === p ? TASK_PRIORITY_COLORS[p] : undefined,
                    color: tfForm.priority === p ? 'white' : undefined,
                  }}
                  onClick={() => { if (isLocked) return; isDirtyRef.current = true; setTfForm(f => ({ ...f, priority: p })); }}
                  type="button"
                  disabled={isLocked}
                >
                  {TASK_PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
            {fieldError('priority') && <span className="form-error-text">{fieldError('priority')}</span>}
          </div>

          <div className="form-group">
            <FormSelect
              label="Phase"
              required
              value={tfForm.phase}
              onChange={e => setTf('phase', e.target.value)}
              error={fieldError('phase')}
              placeholder="Select a phase…"
              disabled={isLocked}
              options={[
                { value: 'foundation', label: 'Foundation' },
                { value: 'design', label: 'Design' },
                { value: 'lock-in', label: 'Lock-In' },
                { value: 'make-order', label: 'Make & Order' },
                { value: 'finalise', label: 'Finalise' },
                { value: 'event-week', label: 'Event Week' },
              ]}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Event{projectEvents.length > 0 && <span className="form-required">*</span>}</label>
          {projectEvents.length > 0 ? (
            <>
              <TagMultiSelect
                values={tfForm.eventIds.map(id => projectEvents.find(e => e.id === id)?.title ?? id)}
                onChange={titles => {
                  isDirtyRef.current = true;
                  // Only keep titles that resolve to a real event id — falling back to the
                  // raw title string here would silently corrupt eventIds, making the task
                  // invisible to every event's task list (it would never match a real id).
                  const ids = titles
                    .map(t => projectEvents.find(e => e.title === t)?.id)
                    .filter((id): id is string => Boolean(id));
                  setTfForm(f => ({ ...f, eventIds: ids }));
                }}
                options={projectEvents.map(e => e.title)}
                placeholder="Select at least one event"
                allowCreate={false}
                disabled={isLocked}
              />
              {fieldError('eventIds') && <span className="form-error-text">{fieldError('eventIds')}</span>}
            </>
          ) : (
            <span className="form-helper">No events yet — create one before this task can be assigned to it.</span>
          )}
        </div>

        <FormInput
          label="Milestone"
          type="text"
          placeholder="e.g. Venue Secured"
          value={tfForm.milestone}
          onChange={e => setTf('milestone', e.target.value)}
          error={fieldError('milestone')}
          required
          disabled={isLocked}
        />

        <FormInput
          label="Deliverable"
          type="text"
          placeholder="e.g. Signed contract and deposit"
          value={tfForm.deliverable}
          onChange={e => setTf('deliverable', e.target.value)}
          error={fieldError('deliverable')}
          required
          disabled={isLocked}
        />

        <RichTextEditor
          label="Notes"
          placeholder="Additional context..."
          value={tfForm.notes}
          onChange={val => setTf('notes', val)}
          error={fieldError('notes')}
          rows={4}
          disabled={isLocked}
        />
      </div>

      {/* Comments plus the task's own status trail (Pending → In progress →
          Done), through the same shared block every other record uses — the
          thread here used to be hand-wired per-handler, and had no history
          beside it at all. */}
      <RecordActivity
        entityType="task"
        entityId={task?.id}
        historyType="task"
        label="Comments"
        statusLabels={TASK_STATUS_LABELS}
        fullWidth
      />

      </div>

      <aside className="detail-sidebar">
        <section className="side-section">
          <FormSelect
            label="Owner Team"
            required
            value={tfForm.ownerTeam}
            onChange={e => setTf('ownerTeam', e.target.value)}
            error={fieldError('ownerTeam')}
            options={[{ value: '', label: '— None —' }, ...TEAM_OPTIONS.map(t => ({ value: t, label: t }))]}
            disabled={isLocked}
          />
          <div className="wt-form-group">
            <label className="form-label">Assignee<span className="form-required">*</span></label>
            <TagMultiSelect
              values={tfForm.assignees}
              onChange={assignees => { isDirtyRef.current = true; setTfForm({ ...tfForm, assignees }); }}
              options={assigneeOptions}
              placeholder="Search team members…"
              allowCreate={false}
              disabled={isLocked}
            />
            {fieldError('assignees') && <span className="form-error-text">{fieldError('assignees')}</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Parent Task</label>
            <SearchableSelect
              value={tfForm.parentTaskId}
              onChange={id => { isDirtyRef.current = true; setTfForm(f => ({ ...f, parentTaskId: id })); }}
              options={[
                { value: '', label: '— None —' },
                ...pickableTasks.map(t => ({ value: t.id, label: taskLabel(t) })),
              ]}
              placeholder="Search tasks…"
              disabled={isLocked}
            />
          </div>
          <div className="wt-form-group">
            <div className="tf-label-row">
              <label className="form-label">Depends On</label>
              {blockingTaskIds.length > 0 && (
                // Deliberately not labelled "Blocked" — that word is also a
                // real, separately-settable value of the Status field above,
                // and a task can carry both at once (a manually-set Blocked
                // status, plus an unfinished dependency), which read as the
                // same fact shown twice. This badge is specifically about
                // the dependency, not the task's own status.
                <span
                  className="tf-blocked-badge"
                  title={`Waiting on unfinished dependenc${blockingTaskIds.length === 1 ? 'y' : 'ies'}: ${blockingTaskIds.map(id => recordLabel('task', id)).join(', ')}`}
                >
                  <Icon name="alert-triangle" size={12} />
                  Waiting on dependency
                </span>
              )}
            </div>
            <TagMultiSelect
              values={dependencyLabels(tfForm.dependsOnTaskIds, otherTasks, taskLabel)}
              onChange={labels => {
                isDirtyRef.current = true;
                const ids = dependencyIdsFromLabels(labels, tfForm.dependsOnTaskIds, otherTasks, taskLabel);
                setTfForm(f => ({ ...f, dependsOnTaskIds: ids }));
              }}
              options={pickableTasks.map(taskLabel)}
              placeholder="Search tasks…"
              allowCreate={false}
              disabled={isLocked}
            />
          </div>
          {childTasks.length > 0 && (
            <div className="tf-subtask-rollup">
              {childTasks.filter(t => t.status === 'Done').length}/{childTasks.length} subtasks done
            </div>
          )}
          {dependentTasks.length > 0 && (
            <div className="wt-form-group">
              <div className="tf-label-row">
                <label className="form-label">Blocks</label>
              </div>
              {/* No count badge here (unlike Depends On) — the chip list
                  below already shows which dependents are unfinished (red)
                  vs done (tf-dependent-done), so a "Blocking N" badge next
                  to the label would just restate what the chips already say. */}
              <div className="tf-dependents-list">
                {dependentTasks.map(t => (
                  <span key={t.id} className={`tf-dependent-chip${t.status === 'Done' ? ' tf-dependent-done' : ''}`}>
                    {taskLabel(t)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <FormDateTimeInput
            label="Starts"
            required
            date={tfForm.startDateISO}
            time={tfForm.startTime}
            onChange={(d, t) => { setTf('startDateISO', d); setTf('startTime', t); }}
            error={fieldError('startDateISO')}
            disabled={isLocked}
          />
          <FormDateTimeInput
            label="Due"
            required
            date={tfForm.dueDateISO}
            time={tfForm.endTime}
            onChange={(d, t) => { setTf('dueDateISO', d); setTf('endTime', t); }}
            error={fieldError('dueDateISO')}
            disabled={isLocked}
          />
        </section>
      </aside>
    </div>
  );
};
